import React, {
  forwardRef,
  HTMLProps,
  PropsWithRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import CustomSelect, { OptionType } from '../components/select';
import { DisplayFormat, Meridiem, Time } from './types';

import './styles.css';
import {
  alignTime,
  generateHourOptions,
  generateMinuteOptions,
  convertHourFrom12HrTo24Hr,
} from './methods';
import TwoDots from '../icons/two-dots';
import moment from 'moment';
import { checkSystemTimeFormatInChrome, checkSystemTimeFormatOtherThenChrome } from 'core/utils/constants';

/**
 * Props for TimePicker React Component
 */
export type TimePickerProps = {
  /**
   * This function is called when the selected date is changed.
   */
  onChange: (time: Time) => void;
  /**
   * The selected date.
   */
  value: Time;
  /**
   * If the TimePicker is disabled
   */
  disabled?: boolean;
  /**
   * Which time format to use
   */
  displayFormat?: DisplayFormat | any;
  /**
   * The number of minutes between each minute select option - default is 30
   */
  minutesInterval?: number;

  error?: boolean;

  message?: string;
} & PropsWithRef<
  Omit<
    HTMLProps<HTMLInputElement>,
    'onChange' | 'selected' | 'options' | 'value' | 'disabled'
  >
>;

const meridiemOptions: OptionType<Meridiem>[] = [
  { value: Meridiem.AM, label: Meridiem.AM, disabled: false },
  { value: Meridiem.PM, label: Meridiem.PM, disabled: false },
];

const formatNumber = (v: number) => v.toString().padStart(2, '0');

function isOperatingSystemTimeFormat12Hour() {
  // // Create a Date object with the current time
  // const now = new Date();

  // // Get the time string according to the user's locale
  // const timeString = now.toLocaleTimeString();
  // // Check if the time string contains "AM" or "PM"
  // if (/AM|PM/.test(timeString)) {
  //   return '12hr';
  // }
  // return '24hr';
  
  // const is24hr = (checkSystemTimeFormatInChrome() || checkSystemTimeFormatOtherThenChrome());
  // if (is24hr)
  //   return "24hr";
  // else
    return "12hr";
}

/**
 * TimePicker React Component
 */
const TimePicker = forwardRef<HTMLDivElement, TimePickerProps>(
  (
    {
      onChange,
      value,
      className,
      disabled,
      displayFormat = isOperatingSystemTimeFormat12Hour(),
      minutesInterval = 1,
      ...props
    },
    ref,
  ) => {
    if (
      typeof minutesInterval !== 'number' &&
      minutesInterval < 1 &&
      Number.isInteger(minutesInterval)
    ) {
      throw new Error('minutesInterval must be an integer greater than 0');
    }

    const [selectedTime, setSelectedTime] = useState(() => {
      if (value !== undefined) return alignTime(value, minutesInterval);
      const d = new Date();
      return alignTime(
        { minutes: d.getMinutes(), hours: d.getHours() },
        minutesInterval,
      );
    });

    const [currentMeridiem, setCurrentMeridiem] = useState<Meridiem>(() =>
      selectedTime.hours <= 11 ? Meridiem.AM : Meridiem.PM,
    );

    const handleMinutesChange = useCallback(
      (v: number) => {
        setSelectedTime(t => {
          return alignTime({ ...t, minutes: v }, minutesInterval);
        });
      },
      [minutesInterval],
    );

    const handleHoursChange = useCallback(
      (v: number) => {
        setSelectedTime(t => {
          let hours = v;
          if (displayFormat === '12hr') {
            hours = convertHourFrom12HrTo24Hr(hours, currentMeridiem);
          }
          return alignTime({ ...t, hours }, minutesInterval);
        });
      },
      [minutesInterval, currentMeridiem, displayFormat],
    );

    const handleMeridiemChange = useCallback((v: Meridiem) => {
      setCurrentMeridiem(v);
      setSelectedTime(t => {
        if (v === Meridiem.AM) {
          return {
            minutes: t.minutes,
            hours: t.hours - 12,
          };
        }
        return {
          minutes: t.minutes,
          hours: t.hours + 12,
        };
      });
    }, []);

    // the array of options of minutes to select from
    const minuteOptions = useMemo(
      () => generateMinuteOptions(minutesInterval),
      [minutesInterval],
    );

    // the array of options of hours to select from
    const hourOptions = useMemo(
      () => generateHourOptions(displayFormat),
      [displayFormat],
    );

    //
    const currentHourDisplayValue = useMemo(() => {
      if (displayFormat === '24hr') return selectedTime.hours;
      const h =
        currentMeridiem === Meridiem.AM
          ? selectedTime.hours
          : selectedTime.hours - 12;
      if (h === 0) return 12;
      return h;
    }, [selectedTime.hours, displayFormat, currentMeridiem]);

    useEffect(() => {
      onChange(selectedTime);
    }, [selectedTime]);

    useEffect(() => {
      setSelectedTime(alignTime(selectedTime, minutesInterval));
    }, [minutesInterval]);

    return (
      <div className="">
        {props.label !== undefined && (
          <label className={`block mb-1 text-sm font-[500]`}>
            {props.label}
            {props.required && <span className={`text-secondary-red`}>*</span>}
          </label>
        )}
        <div
          // stp-border-red
          className={`${props?.height ? props?.height : 'h-[44px]'
            } p-[12px] flex justify-center items-center stp ${!!props?.error ? 'stp-border-red' : ''
            } ${className ?? ''} ${disabled ? 'stp--disabled' : ''}`}
          {...props}
          ref={ref}
        >
          <CustomSelect
            disabled={disabled}
            value={currentHourDisplayValue}
            onChange={handleHoursChange}
            options={hourOptions}
            formatValue={formatNumber}
          />
          <span
            className={`stp--divider ${disabled ? 'stp--divider__disabled' : ''
              }`}
          >
            <TwoDots />
          </span>
          <CustomSelect
            disabled={disabled}
            value={selectedTime.minutes}
            onChange={handleMinutesChange}
            options={minuteOptions}
            formatValue={formatNumber}
          />
          {displayFormat === '12hr' && (
            <CustomSelect
              disabled={disabled}
              value={currentMeridiem}
              onChange={handleMeridiemChange}
              options={meridiemOptions}
            />
          )}
        </div>
        {!!props?.message && !!props?.error && (
          <div>
            <span
              className={classNames(
                `${props.error ? 'text-secondary-red' : 'text-neutral-60'}
            mt-[4px] text-xs
            `,
              )}
            >
              {props.message}
            </span>
          </div>
        )}
      </div>
    );
  },
);

TimePicker.displayName = 'TimePicker';

export default TimePicker;